/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { TEMPLATES, COMPONENT_TEMPLATES } from "./widgetTypes";
import { rankMetrics } from "./useDashboardMetrics";
import MetricPickGrid from "./MetricPickGrid";
import { IcClose, IcBack, IcPlus, IcCheck } from "./icons";
import { WIDGETS } from "../../Homepage/customize/widgetRegistry";

/* The game components an Analytics dashboard can hold — the same set the
   builder's add drawer offers (personal cards left out; a shared/TV board must
   not read one signed-in person's numbers). Resolved from the widget registry
   so titles, blurbs and thumbnails stay in one place. */
const COMPONENT_IDS = ["beatyeti", "yeti", "leaderboard", "podium", "kotm", "earnings", "milestones"];
const COMPONENTS = COMPONENT_IDS
  .map((id) => WIDGETS.find((w) => w.id === id))
  .filter(Boolean);

/* Warmer one-liners than the terse template descs — copy that tells you
   when you'd reach for each layout. */
const RICH_DESC = {
  overview: "Your four headline numbers with one big trend under them. The everyday favorite.",
  trends: "Two charts side by side \u2014 perfect for watching a pair of metrics race.",
  kpiwall: "A tight grid of key numbers. Best for the office TV and quick scans.",
  report: "A trend beside the raw numbers \u2014 for when you want the table too.",
  focus: "One hero chart with three supporting numbers. One story, told big.",
  insights: "Numbers, a comparison band, and distribution \u2014 the analyst\u2019s pick.",
  weekly: "Gauge, heatmap, and bars \u2014 the Monday-morning summary in one view.",
};

/* Tiny schematic of a layout: just the slot shapes, no chart art. */
function Schema({ tpl }) {
  const rows = tpl.slots.reduce((m, sl) => Math.max(m, sl.y + sl.h), 0) || 1;
  return (
    <span className="db-schema" aria-hidden="true">
      {tpl.slots.map((sl) => (
        <i
          key={`${sl.x}-${sl.y}-${sl.w}-${sl.h}`}
          style={{
            left: `${(sl.x / 12) * 100 + 2}%`,
            top: `${(sl.y / rows) * 100 + 4}%`,
            width: `${(sl.w / 12) * 100 - 4}%`,
            height: `${(sl.h / rows) * 100 - 8}%`,
            background: sl.t === "stat" ? "#dfe7f0" : "#e9f0f8",
          }}
        />
      ))}
    </span>
  );
}
Schema.propTypes = { tpl: PropTypes.object.isRequired };

/* Mini schematic of a component (role) template — the real components' shapes,
   so the card reads as a ready board, not a chart layout. */
function CompSchema({ tpl }) {
  const rows = tpl.components.reduce((m, c) => Math.max(m, c.y + c.h), 0) || 1;
  return (
    <span className="db-schema" aria-hidden="true">
      {tpl.components.map((c, i) => (
        <i
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            left: `${(c.x / 12) * 100 + 2}%`,
            top: `${(c.y / rows) * 100 + 4}%`,
            width: `${(c.w / 12) * 100 - 4}%`,
            height: `${(c.h / rows) * 100 - 8}%`,
            background: "#dbe6f3",
          }}
        />
      ))}
    </span>
  );
}
CompSchema.propTypes = { tpl: PropTypes.object.isRequired };

/* The wizard's steps, in order. */
const STEPS = [
  { n: 1, label: "Layout" },
  { n: 2, label: "Metrics" },
];

/**
 * Guided "New dashboard" flow in one glass modal:
 *   Step 1 — choose a layout (template gallery, or blank)
 *   Step 2 — name it and pick the metrics that fill it
 * The dashboard record is only created at the end, fully assembled — no more
 * empty "Untitled dashboard" litter, and the template→metrics relationship
 * is explicit. Unfilled slots become ghost placeholders on the board.
 */
export default function NewDashboardWizard({ open, metricsApi, onCreate, onClose, busy }) {
  const { metrics, getSummary } = metricsApi;
  const [step, setStep] = useState(1);
  const [tplId, setTplId] = useState(null); // template id, "blank", or null
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [comps, setComps] = useState(() => new Set()); // chosen game components
  const [pane, setPane] = useState("metrics"); // step-2 tab: metrics | components
  const [q, setQ] = useState(""); // one search box, filters the active pane

  useEffect(() => {
    if (open) {
      setStep(1);
      setTplId(null);
      setName("");
      setSelected(new Set());
      setComps(new Set());
      setPane("metrics");
      setQ("");
    }
  }, [open]);

  const ranked = useMemo(() => rankMetrics(metrics, getSummary), [metrics, getSummary]);

  if (!open) return null;

  const tpl = TEMPLATES.find((t) => t.id === tplId) || null;
  /* A component (role) template is picked the same way, but it's a ready board —
     no metric step, so it creates straight from Step 1. */
  const compTpl = COMPONENT_TEMPLATES.find((t) => t.id === tplId) || null;
  const slots = tpl?.slots?.length || 0;
  const count = selected.size;

  const createRole = () => {
    if (busy || !compTpl) return;
    onCreate({ name: name.trim() || compTpl.name, componentTemplateId: compTpl.id });
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleComp = (id) => {
    setComps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = () => {
    if (busy) return;
    onCreate({
      name: name.trim() || (tpl ? `${tpl.name} dashboard` : "Untitled dashboard"),
      templateId: tpl?.id || "",
      metricIds: ranked.filter((m) => selected.has(m.id)).map((m) => m.id),
      componentIds: COMPONENTS.filter((c) => comps.has(c.id)).map((c) => c.id),
    });
  };

  return (
    <div
      className="db-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="db-modal db-modal-wide" role="dialog" aria-modal="true" aria-label="New dashboard">
        <div className="db-modal-inner">
          <div className="db-modal-head">
            <div>
              <div className="db-modal-t">New dashboard</div>
              <div className="db-modal-s">
                {step === 1 && "Pick where to start \u2014 every layout is fully editable after."}
                {step === 2 && "Add metrics, components, or both \u2014 it\u2019s fully editable after."}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {step === 2 && tpl && (
                <span className={`db-slotpill ${count >= slots ? "full" : ""}`}>
                  {count} of {slots} picked
                </span>
              )}
              <button type="button" className="db-iconbtn" onClick={onClose} aria-label="Close">
                <IcClose />
              </button>
            </div>
          </div>

          <div className="db-stepper" aria-label={`Step ${step} of ${STEPS.length}`}>
            {STEPS.map((s, i) => {
              let state = "todo";
              if (s.n < step) state = "done";
              else if (s.n === step) state = "active";
              return (
                <React.Fragment key={s.n}>
                  {i > 0 && <span className={`db-step-line ${s.n <= step ? "on" : ""}`} />}
                  <button
                    type="button"
                    className={`db-step ${state}`}
                    disabled={s.n >= step}
                    onClick={() => s.n < step && setStep(s.n)}
                  >
                    <span className="db-step-dot">
                      {state === "done" ? <IcCheck size={12} /> : s.n}
                    </span>
                    <span className="db-step-label">{s.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {step === 1 ? (
            <>
            {COMPONENT_TEMPLATES.length > 0 && (
              <>
                <div className="db-wiz-grouplab">Team boards <span>ready-made, from your components</span></div>
                <div className="db-wiz2">
                  {COMPONENT_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="db-tpl2 rolecard"
                      aria-pressed={tplId === t.id}
                      onClick={() => setTplId(t.id)}
                    >
                      <CompSchema tpl={t} />
                      <span className="tx">
                        <span className="n">{t.name}</span>
                        <span className="d">{t.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="db-wiz-grouplab">Metric layouts <span>pick a shape, fill it with metrics</span></div>
              </>
            )}
            <div className="db-wiz2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="db-tpl2"
                  aria-pressed={tplId === t.id}
                  onClick={() => setTplId(t.id)}
                >
                  <Schema tpl={t} />
                  <span className="tx">
                    <span className="n">{t.name}</span>
                    <span className="d">{RICH_DESC[t.id] || t.desc}</span>
                  </span>
                </button>
              ))}
              <button
                type="button"
                className="db-tpl2 blankcard"
                aria-pressed={tplId === "blank"}
                onClick={() => setTplId("blank")}
              >
                <span className="db-schema plus">
                  <IcPlus size={15} />
                </span>
                <span className="tx">
                  <span className="n">Blank</span>
                  <span className="d">Start from nothing and build it your way.</span>
                </span>
              </button>
            </div>
            </>
          ) : (
            <>
              <input
                className="db-input"
                value={name}
                placeholder={tpl ? `${tpl.name} dashboard` : "Dashboard name"}
                aria-label="Dashboard name"
                onChange={(e) => setName(e.target.value)}
              />
              {/* Metrics OR components — a dashboard holds both, so Step 2 is a
                  tabbed picker. The compact toggle and ONE search sit on a
                  single toolbar row; the search filters whichever pane is open. */}
              <div className="db-wiz-toolbar">
                <div className="db-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={pane === "metrics"}
                    className={`db-tab ${pane === "metrics" ? "on" : ""}`}
                    onClick={() => setPane("metrics")}
                  >
                    Metrics{selected.size ? ` · ${selected.size}` : ""}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={pane === "components"}
                    className={`db-tab ${pane === "components" ? "on" : ""}`}
                    onClick={() => setPane("components")}
                  >
                    Components{comps.size ? ` · ${comps.size}` : ""}
                  </button>
                </div>
                <div className="db-wiz-search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={pane === "metrics" ? "Search metrics" : "Search components"}
                    aria-label={pane === "metrics" ? "Search metrics" : "Search components"}
                  />
                </div>
              </div>

              {pane === "metrics" ? (
                <MetricPickGrid
                  metrics={ranked}
                  getSummary={getSummary}
                  selected={selected}
                  onToggle={toggle}
                  query={q}
                  hideSearch
                />
              ) : (
                <div className="db-comp-grid" role="listbox" aria-label="Components">
                  {COMPONENTS.filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase())).map((c) => {
                    const on = comps.has(c.id);
                    const Thumb = c.thumb;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        role="option"
                        aria-selected={on}
                        className={`db-comp ${on ? "on" : ""}`}
                        onClick={() => toggleComp(c.id)}
                      >
                        <span className="db-comp-pv" aria-hidden="true">
                          {Thumb ? <Thumb /> : null}
                        </span>
                        <span className="db-comp-tx">
                          <span className="db-comp-n">{c.title}</span>
                          <span className="db-comp-d">{c.blurb}</span>
                        </span>
                        <span className={`db-comp-tick ${on ? "on" : ""}`} aria-hidden="true">
                          {on ? <IcCheck size={12} /> : <IcPlus size={12} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div className="db-modal-foot">
            {/* Progress lives in the stepper up top — the footer is just the
                actions, one consistent shape on both steps: Back on the left,
                Cancel + primary on the right. The old footer printed a second
                "Step X of 2" in a different place on each step, which is what
                read as broken. */}
            {step === 2 && (
              <button type="button" className="db-btn ghost" style={{ marginRight: "auto" }} onClick={() => setStep(1)}>
                <IcBack size={14} />
                Back
              </button>
            )}
            <button type="button" className="db-btn ghost" onClick={onClose}>
              Cancel
            </button>
            {/* Step 1: a Team board creates straight away; a metric layout goes
                on to pick metrics. Step 2 always creates. */}
            {step === 1 && compTpl && (
              <button type="button" className="db-btn primary" disabled={busy} onClick={createRole}>
                {busy ? "Creating…" : "Create dashboard"}
              </button>
            )}
            {step === 1 && !compTpl && (
              <button type="button" className="db-btn primary" disabled={!tplId} onClick={() => setStep(2)}>
                Continue
              </button>
            )}
            {step === 2 && (
              <button type="button" className="db-btn primary" disabled={busy} onClick={create}>
                {busy ? "Creating…" : "Create dashboard"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

NewDashboardWizard.propTypes = {
  open: PropTypes.bool,
  metricsApi: PropTypes.object.isRequired,
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
